import React, { useRef, Suspense, useEffect } from "react";
import { RigidBody } from "@react-three/rapier";
import * as THREE from "three";
import DialogPerson from "../../../components/DialogPerson";
import { usePersonModel } from "../../../hooks/usePersonModel";
import { usePersonAnimation } from "../../../hooks/usePersonAnimation";
import { usePersonInteraction } from "../../../hooks/usePersonInteraction";
import { usePersonMovement } from "../../../hooks/usePersonMovement";
import { PersonUI } from "./PersonUI";

type PersonProps = {
  npc: {
    id?: string;
    position: [number, number, number];
    text: string;
    route: [number, number, number][];
    color: string;
    modelURL: string;
    name?: string;
    dialogId?: string;
    isUnlocked?: boolean;
    isCompleted?: boolean;
    onComplete?: () => void;
    gltf?: any;
    map?: number;
    prerequisiteNpcId?: string | null;
  };
  mapId: string;
}

export default function Person({ npc, mapId }: PersonProps) {
  const { id, position, route, color, name, dialogId, isUnlocked = true, isCompleted = false, onComplete, gltf } = npc;
  const npcId = id || "unknown";
  const dialogIdToLoad = dialogId || npcId;

  const rigidBodyRef = useRef<any>(null);
  const animationRef = useRef<THREE.Object3D>(null);

  // Model
  const { clonedScene, animations } = usePersonModel(gltf);

  // Interaction
  const { isFetchingDialog, handleStartInteraction, dialogIsOpen } = usePersonInteraction({
    npcId,
    dialogIdToLoad,
    npcName: name || "Character",
    mapId,
    isUnlocked,
    onComplete,
    rigidBodyRef
  });

  // Movement & Physics
  const { isStopped, showText, isLookingAtPlayer } = usePersonMovement(
    rigidBodyRef,
    position,
    route,
    npcId,
    1.5,
    3,
    3
  );

  // Animation
  const animationIndex = (isStopped || showText) ? 0 : 1;
  const { changeAnimation } = usePersonAnimation(animations, animationRef);

  useEffect(() => {
    changeAnimation(animationIndex);
  }, [animationIndex, changeAnimation]);

  // Enter key to start dialog when player is near
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Enter' && showText && !dialogIsOpen && isUnlocked) {
        handleStartInteraction();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showText, dialogIsOpen, isUnlocked, handleStartInteraction]);

  return (
    <RigidBody 
      ref={rigidBodyRef} 
      position={position}
      colliders="hull"
      mass={2}
      friction={0.7}
      restitution={1}
      linearDamping={0.5}
      angularDamping={0.8}
      enabledRotations={[false, true, false]} // Only allow Y rotation
      lockRotations={true} // Lock X and Z rotations
    >
      {/* GLTF Model */}
      <Suspense fallback={
        <mesh castShadow position={[0, 0.7, 0]}>
          <capsuleGeometry args={[0.3, 0.7]} />
          <meshStandardMaterial color={color} />
        </mesh>
      }>
        {clonedScene ? (
          <group position={[0, -1, 0]} onClick={handleStartInteraction}>
            <primitive 
              ref={animationRef}
              object={clonedScene}
              scale={[1.3, 1.3, 1.3]}
              receiveShadow
              castShadow
            />
          </group>
        ) : (
          <group ref={animationRef as any} onClick={handleStartInteraction}>
            <mesh castShadow>
              <capsuleGeometry args={[0.3, 0.7]} />
              <meshStandardMaterial color={color} />
            </mesh>
          </group>
        )}
      </Suspense>
      
      {/* Status indicator above Person */}
      <PersonUI 
        showText={showText}
        dialogIsOpen={dialogIsOpen}
        isUnlocked={isUnlocked}
        isCompleted={isCompleted}
        isLookingAtPlayer={isLookingAtPlayer}
        name={name}
        onInteract={handleStartInteraction}
      />

      {/* Dialog UI */}
      <DialogPerson npcId={npcId} />
    </RigidBody>
  );
}

